// bm-design-system: table primitive
// Tabular records with columns — for key/value detail lists use DataTable (a
// <dl>) instead. TableRow accepts an href to make the whole row navigate; by
// convention the lead cell should also contain a real <Link> to the same URL
// so keyboard and middle-click navigation keep working.
import * as React from "react";
import { router } from "@inertiajs/react";
import { cn } from "@/lib/utils";

const Table = React.forwardRef<
  HTMLTableElement,
  React.TableHTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="overflow-x-auto rounded-lg border border-hairline bg-page">
    <table ref={ref} className={cn("w-full text-sm", className)} {...props} />
  </div>
));
Table.displayName = "Table";

const TableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("border-b border-hairline", className)} {...props} />
));
TableHead.displayName = "TableHead";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("divide-y divide-hairline", className)} {...props} />
));
TableBody.displayName = "TableBody";

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** Navigate to this URL when the row is clicked (except on links/controls inside it). */
  href?: string;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, href, onClick, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(href && "cursor-pointer transition-colors hover:bg-surface", className)}
      onClick={(e) => {
        onClick?.(e);
        if (href && !e.defaultPrevented) {
          const target = e.target as HTMLElement;
          if (!target.closest("a,button,input,select,textarea,label")) {
            router.visit(href);
          }
        }
      }}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHeadCell = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      "px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted",
      className,
    )}
    {...props}
  />
));
TableHeadCell.displayName = "TableHeadCell";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={cn("px-4 py-3", className)} {...props} />
));
TableCell.displayName = "TableCell";

export { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell };
